import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  Box,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

interface MarkdownContentProps {
  content: string;
}

const StyledCode = styled("code")(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  fontFamily: "monospace",
  fontSize: "0.875rem",
}));

const StyledPre = styled("pre")(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflow: "auto",
  "& code": {
    backgroundColor: "transparent",
    padding: 0,
  },
}));

const components: Components = {
  // h1: ({ node: _, ref: _ref, ...props }) => (
  //   <Typography variant="h3" component="h1" gutterBottom {...props} />
  // ),
  h1: ({ node: _, ref: _ref }) => null,
  h2: ({ node: _, ref: _ref, ...props }) => (
    <Typography
      color="text.primary"
      component="h2"
      sx={{ "&:not(first-child)": { mt: 4, mb: 2, textWrap: "balance" } }}
      textAlign="center"
      variant="h6"
      {...props}
    />
  ),
  h3: ({ node: _, ref: _ref, ...props }) => (
    <Typography
      color="text.primary"
      component="h3"
      sx={{ "&:not(first-child)": { mt: 3, mb: 1, textWrap: "balance" } }}
      variant="subtitle1"
      {...props}
    />
  ),
  h4: ({ node: _, ref: _ref, ...props }) => (
    <Typography
      color="text.primary"
      component="h4"
      sx={{ "&:not(first-child)": { mt: 2, mb: 1, textWrap: "balance" } }}
      variant="subtitle2"
      {...props}
    />
  ),
  // h5: ({ node: _, ref: _ref, ...props }) => (
  //   <Typography
  //     color="text.primary"
  //     variant="subtitle1"
  //     component="h5"
  //     gutterBottom
  //     sx={{ mt: 2 }}
  //     {...props}
  //   />
  // ),
  // h6: ({ node: _, ref: _ref, ...props }) => (
  //   <Typography
  //     color="text.primary"
  //     variant="subtitle2"
  //     component="h6"
  //     gutterBottom
  //     sx={{ mt: 1 }}
  //     {...props}
  //   />
  // ),
  p: ({ node: _, ref: _ref, ...props }) => (
    <Typography
      color="text.secondary"
      display="block"
      textAlign="justify"
      variant="caption"
      {...props}
    />
  ),
  a: ({ node: _, ref: _ref, href, children, ...props }) => (
    <Link
      href={href}
      rel="noopener noreferrer"
      target="_blank"
      variant="caption"
      {...props}
    >
      {children}
    </Link>
  ),
  ul: ({ node: _, ref: _ref, ...props }) => (
    <Box component="ul" sx={{ pl: 2, my: 1 }} {...props} />
  ),
  ol: ({ node: _, ref: _ref, ...props }) => (
    <Box component="ol" sx={{ pl: 2, my: 1 }} {...props} />
  ),
  li: ({ node: _, ref: _ref, ...props }) => (
    <Typography
      color="text.secondary"
      component="li"
      sx={{ mb: 0.5 }}
      textAlign="left"
      variant="caption"
      {...props}
    />
  ),
  hr: () => <Divider sx={{ my: 3 }} />,
  strong: ({ node: _, ref: _ref, ...props }) => (
    <Typography
      component="strong"
      display="inline"
      fontWeight={500}
      variant="caption"
      {...props}
    />
  ),
  em: ({ node: _, ref: _ref, ...props }) => (
    <Typography
      component="em"
      display="inline"
      fontStyle="italic"
      variant="caption"
      {...props}
    />
  ),
  blockquote: ({ node: _, ref: _ref, ...props }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: 4,
        borderColor: "divider",
        pl: 2,
        ml: 0,
        my: 2,
        color: "text.secondary",
      }}
      {...props}
    />
  ),
  code: ({ node: _, ref: _ref, className, children, ...props }) => {
    // If it's a code block (has a className like "language-xxx")
    if (className) {
      return (
        <StyledPre>
          <code className={className} {...props}>
            {children}
          </code>
        </StyledPre>
      );
    }
    // Inline code
    return <StyledCode {...props}>{children}</StyledCode>;
  },
  table: ({ node: _, ref: _ref, ...props }) => (
    <TableContainer sx={{ my: 2 }}>
      <Table size="small" {...props} />
    </TableContainer>
  ),
  thead: ({ node: _, ref: _ref, ...props }) => <TableHead {...props} />,
  tbody: ({ node: _, ref: _ref, ...props }) => <TableBody {...props} />,
  tr: ({ node: _, ref: _ref, ...props }) => <TableRow {...props} />,
  td: ({ node: _, ref: _ref, align: _align, ...props }) => (
    <TableCell {...props} />
  ),
  th: ({ node: _, ref: _ref, align: _align, ...props }) => (
    <TableCell component="th" {...props} />
  ),
};

export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
}) => {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
};
